import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import { ConfirmationData, ComparisonResult } from '../../types/confirmation';
import './EmailPreviewPanel.css';

interface EmailPreviewPanelProps {
  confirmationData: ConfirmationData | null;
  loading: boolean;
  error: string | null;
  // Highlighting props
  selectedField?: string | null;
  highlightText?: string;  // The raw text to find and highlight
  occurrenceIndex?: number;  // Which occurrence (0-based) to highlight (KAN-147)
  comparisonResult?: ComparisonResult;
}

const EmailPreviewPanel: React.FC<EmailPreviewPanelProps> = ({
  confirmationData,
  loading,
  error,
  selectedField,
  highlightText,
  occurrenceIndex = 0,
  comparisonResult,
}) => {
  const { t } = useTranslation();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const bodyRef = useRef<any>(null);

  // Scroll to the first highlight when selectedField changes
  useEffect(() => {
    if (bodyRef.current && highlightText && selectedField) {
      const highlightElement = bodyRef.current.querySelector('.email-highlight');
      if (highlightElement) {
        highlightElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [selectedField, highlightText]);

  if (loading) {
    return (
      <div className="email-preview-loading">
        <div className="spinner" />
        <p>{t('confirmations.loadingConfirmation')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="email-preview-error">
        <p>{error}</p>
      </div>
    );
  }

  if (!confirmationData) {
    return (
      <div className="email-preview-empty">
        <p>{t('confirmations.selectTradeToView')}</p>
      </div>
    );
  }

  // Determine highlight class based on comparison result (used by both API and email views)
  const highlightClass = comparisonResult === 'match' ? 'highlight-match' :
                         comparisonResult === 'mismatch' ? 'highlight-mismatch' :
                         'highlight-pending';

  // Render JSON with a highlighted line for the selected field
  const renderJsonWithHighlight = (
    obj: Record<string, unknown>,
    field: string | null,
    hlClass: string,
  ): React.ReactNode => {
    const entries = Object.entries(obj);
    if (entries.length === 0) return '{}';

    const lines: React.ReactNode[] = ['{'];
    entries.forEach(([key, val], i) => {
      const comma = i < entries.length - 1 ? ',' : '';
      const lineText = `  "${key}": ${JSON.stringify(val)}${comma}`;
      const isHighlighted = field === key;

      lines.push(
        <div
          key={key}
          className={isHighlighted ? `api-json-line-highlight ${hlClass}` : undefined}
          ref={isHighlighted ? (el) => el?.scrollIntoView({ behavior: 'smooth', block: 'center' }) : undefined}
        >
          {lineText}
        </div>
      );
    });
    lines.push('}');

    return lines;
  };

  // --- Counterparty API view ---
  if (confirmationData.processingSource === 'counterparty_api') {
    const apiMeta = confirmationData.counterpartyApiMetadata;
    const extracted = confirmationData.extractedFields || {};

    // Build a clean JSON representation of the submitted trade fields
    // (exclude internal/derived fields, show only what the bank sent)
    const submittedFields: Record<string, unknown> = {};
    const fieldOrder = [
      'BankTradeNumber', 'ProductType', 'Direction',
      'Currency1', 'QuantityCurrency1', 'Currency2', 'QuantityCurrency2',
      'Price', 'TradeDate', 'ValueDate', 'MaturityDate',
      'PaymentDate', 'Currency1PaymentDate', 'Currency2PaymentDate',
      'Duration', 'SettlementType', 'SettlementCurrency',
      'FixingReference', 'FixingOffset',
      'IsAverageRateForward', 'AverageRateFixingDates',
      'fixingDateRangeStart', 'fixingDateRangeEnd',
      'IsForwardStarting', 'SpotPrice', 'ForwardPoints', 'SpotFixingReference',
    ];
    for (const key of fieldOrder) {
      const val = extracted[key as keyof typeof extracted];
      if (val !== undefined && val !== null && val !== '') {
        submittedFields[key] = val;
      }
    }

    // Format date as DD/MM/YYYY HH:MM:SS
    const formatApiDate = (dateStr: string, timeStr: string): string => {
      if (!dateStr) return '';
      // dateStr is typically yyyy-mm-dd from the backend
      const parts = dateStr.split('-');
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]} ${timeStr || ''}`.trim();
      }
      return `${dateStr} ${timeStr || ''}`.trim();
    };

    return (
      <div className="email-preview api-payload-preview">
        <div className="api-metadata">
          {apiMeta?.counterpartyName && (
            <div className="metadata-row">
              <span className="label">{t('confirmations.apiCounterparty')}:</span>
              <span className="value">{apiMeta.counterpartyName}</span>
            </div>
          )}
          {apiMeta?.clientTaxId && (
            <div className="metadata-row">
              <span className="label">{t('confirmations.apiClientTaxId')}:</span>
              <span className="value">{apiMeta.clientTaxId}</span>
            </div>
          )}
          <div className="metadata-row">
            <span className="label">{t('confirmations.apiReceivedAt')}:</span>
            <span className="value">
              {formatApiDate(confirmationData.emailDate, confirmationData.emailTime)}
            </span>
          </div>
        </div>

        <div className="email-divider" />

        <div className="api-payload-section-title">
          {t('confirmations.apiSubmittedFields')}
        </div>
        <pre className="api-payload-json" ref={bodyRef}>
          {renderJsonWithHighlight(submittedFields, selectedField || null, highlightClass)}
        </pre>
      </div>
    );
  }

  // --- Standard email view ---
  // Detect if body contains actual HTML tags (not just angle brackets from email addresses)
  // Look for common HTML tags like <html>, <body>, <p>, <br>, <div>, <table>, etc.
  const htmlTagPattern = /<(html|body|head|div|p|br|table|tr|td|span|a|b|i|u|strong|em|h[1-6]|ul|ol|li|img|style)[^>]*>/i;
  const isHtml = htmlTagPattern.test(confirmationData.body);

  // Function to escape special regex characters
  const escapeRegex = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // Apply text highlighting — only wrap the Nth occurrence (KAN-147)
  const applyHighlighting = (text: string): string => {
    if (!highlightText || !selectedField) return text;

    try {
      const escapedText = escapeRegex(highlightText);
      const regex = new RegExp(escapedText, 'gi');
      const markTag = `<mark class="email-highlight ${highlightClass}">`;
      const targetIdx = occurrenceIndex;
      let matchCount = 0;

      return text.replace(regex, (match) => {
        const currentIdx = matchCount++;
        if (currentIdx === targetIdx) {
          return `${markTag}${match}</mark>`;
        }
        return match;
      });
    } catch {
      // If regex fails, return original text
      return text;
    }
  };

  const bodyWithHighlights = applyHighlighting(confirmationData.body);
  const sanitizedBody = isHtml
    ? DOMPurify.sanitize(bodyWithHighlights, { ADD_TAGS: ['mark'], ADD_ATTR: ['class'] })
    : bodyWithHighlights;

  return (
    <div className="email-preview">
      <div className="email-metadata">
        <div className="metadata-row">
          <span className="label">{t('confirmations.from')}:</span>
          <span className="value">{confirmationData.senderEmail}</span>
        </div>
        <div className="metadata-row">
          <span className="label">{t('confirmations.date')}:</span>
          <span className="value">
            {confirmationData.emailDate} {confirmationData.emailTime}
          </span>
        </div>
        <div className="metadata-row">
          <span className="label">{t('confirmations.subject')}:</span>
          <span className="value">{confirmationData.subject}</span>
        </div>
      </div>
      <div className="email-divider" />
      {isHtml || highlightText ? (
        <div
          ref={bodyRef}
          className={`email-body ${isHtml ? 'html-content' : 'plain-text'}`}
          dangerouslySetInnerHTML={{ __html: sanitizedBody }}
        />
      ) : (
        <div className="email-body plain-text">{confirmationData.body}</div>
      )}
    </div>
  );
};

export default EmailPreviewPanel;
